import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { blogPublicAPI, blogImageURL } from '../lib/blogApi';
import { Loader2, Calendar, Clock, ArrowLeft, Share2, Sparkles, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setLoading(true);
    blogPublicAPI.get(slug)
      .then(r => {
        setPost(r.data);
        // inject SEO meta dynamically
        const seo = r.data;
        document.title = `${seo.title} — DotCall Blog`;
        const setMeta = (n, c, prop = false) => {
          if (!c) return;
          const a = prop ? 'property' : 'name';
          let el = document.querySelector(`meta[${a}="${n}"]`);
          if (!el) { el = document.createElement('meta'); el.setAttribute(a, n); document.head.appendChild(el); }
          el.setAttribute('content', c);
        };
        setMeta('description', seo.meta_description);
        setMeta('keywords', seo.meta_keywords);
        setMeta('og:title', seo.title, true);
        setMeta('og:description', seo.meta_description, true);
        if (seo.featured_image_url) setMeta('og:image', blogImageURL(seo.featured_image_url), true);
        setMeta('og:type', 'article', true);
        setMeta('twitter:card', 'summary_large_image');
        setMeta('twitter:title', seo.title);
        setMeta('twitter:description', seo.meta_description);
        if (seo.featured_image_url) setMeta('twitter:image', blogImageURL(seo.featured_image_url));

        // JSON-LD article schema
        const ld = {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: seo.title,
          description: seo.meta_description,
          image: seo.featured_image_url ? blogImageURL(seo.featured_image_url) : undefined,
          datePublished: seo.published_at,
          dateModified: seo.updated_at,
          author: { '@type': 'Organization', name: seo.author || 'DotCall' },
          publisher: { '@type': 'Organization', name: 'DotCall' },
        };
        let s = document.querySelector('script[data-seo="article-ld"]');
        if (!s) { s = document.createElement('script'); s.setAttribute('type', 'application/ld+json'); s.setAttribute('data-seo', 'article-ld'); document.head.appendChild(s); }
        s.textContent = JSON.stringify(ld);

        // Load related posts in same category
        blogPublicAPI.list({ category: r.data.category, per_page: 4 })
          .then(rel => setRelated((rel.data.posts || []).filter(p => p.id !== r.data.id).slice(0, 3)))
          .catch(() => {});
      })
      .catch(() => {
        toast.error('Post not found');
        navigate('/blog');
      })
      .finally(() => setLoading(false));

    return () => {
      const ld = document.querySelector('script[data-seo="article-ld"]');
      if (ld) ld.remove();
    };
  }, [slug, navigate]);

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: post.title, text: post.excerpt, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return ''; }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#020617] flex items-center justify-center"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  }
  if (!post) return null;

  return (
    <div className="min-h-screen bg-[#020617] text-white" data-testid="blog-post-page">
      <article className="max-w-4xl mx-auto px-6 pt-28 pb-20">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400 transition-colors" data-testid="back-to-blog">
          <ArrowLeft className="w-4 h-4" /> Back to all articles
        </Link>

        <div className="mt-8 flex items-center gap-3">
          {post.category && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20">{post.category}</Badge>}
          {post.ai_generated && <span className="inline-flex items-center gap-1 text-xs text-purple-400"><Sparkles className="w-3 h-3" />AI-curated</span>}
        </div>

        <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] leading-tight" data-testid="blog-title">
          {post.title}
        </h1>

        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-400">
          <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />{formatDate(post.published_at || post.created_at)}</span>
          <span className="flex items-center gap-2"><Clock className="w-4 h-4" />{post.reading_time || 5} min read</span>
          <span>By {post.author || 'DotCall Team'}</span>
          <Button onClick={handleShare} variant="ghost" size="sm" className="ml-auto text-slate-400 hover:text-amber-400" data-testid="share-button">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>

        {post.featured_image_url && (
          <div className="mt-10 rounded-2xl overflow-hidden border border-white/5 aspect-video bg-[#0B1221]">
            <img src={blogImageURL(post.featured_image_url)} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="prose prose-invert prose-lg max-w-none mt-10
                        prose-headings:font-['Outfit'] prose-headings:text-white
                        prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:border-l-4 prose-h2:border-amber-500 prose-h2:pl-4
                        prose-h3:text-xl prose-h3:text-amber-300 prose-h3:mt-8
                        prose-p:text-slate-300 prose-p:leading-relaxed
                        prose-a:text-amber-400 prose-a:no-underline hover:prose-a:underline
                        prose-strong:text-white
                        prose-code:text-amber-300 prose-code:bg-white/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-[''] prose-code:after:content-['']
                        prose-blockquote:border-amber-500 prose-blockquote:text-slate-300
                        prose-ul:text-slate-300 prose-ol:text-slate-300
                        prose-li:marker:text-amber-500"
             data-testid="blog-content">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>

        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-white/5 flex flex-wrap items-center gap-2">
            <Tag className="w-4 h-4 text-slate-500" />
            {post.tags.map(t => (
              <span key={t} className="px-3 py-1 text-xs bg-white/5 text-slate-400 rounded-full">{t}</span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 p-8 rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 to-orange-500/5 text-center">
          <h3 className="text-2xl font-bold font-['Outfit']">Try DotCall free for 7 days</h3>
          <p className="mt-2 text-slate-400">Launch AI voice agents that speak Hindi, English & 9+ Indian languages.</p>
          <Link to="/register">
            <Button className="mt-5 bg-amber-500 hover:bg-amber-600 text-black font-semibold px-8" data-testid="post-cta-register">
              Start Free Trial
            </Button>
          </Link>
        </div>

        {related.length > 0 && (
          <div className="mt-16">
            <h3 className="text-xl font-bold font-['Outfit'] mb-6">Related Articles</h3>
            <div className="grid md:grid-cols-3 gap-5">
              {related.map(r => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="block p-5 bg-[#0B1221]/80 border border-white/5 rounded-xl hover:border-amber-500/40 transition-all" data-testid={`related-${r.slug}`}>
                  <h4 className="text-sm font-semibold text-white line-clamp-2 mb-2">{r.title}</h4>
                  <p className="text-xs text-slate-500">{formatDate(r.published_at)}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
